/*
 * LXIII Shopify Forms styler.
 * The Shopify Forms app renders inside the <shopify-forms-embed> shadow DOM,
 * so theme CSS cannot reach it. This script injects a stylesheet into the
 * document head (host wrappers) and into every form shadow root (form UI),
 * re-skinning the form to the LXIII Personalization design.
 *
 * Field layout relies on the field IDs the app generates from the field
 * labels (first_name, last_name, email, phone_number, custom#country,
 * custom#address_line_1, custom#city, custom#province, custom#postal_code,
 * custom#receipt_upload). Renaming a field in the app changes its ID.
 */
(function () {
  'use strict';

  var HOST_STYLES = [
    'shopify-forms-embed,',
    '[id^="shopify-block-"][class*="shopify-app-block"],',
    '[data-form-root],',
    '[data-forms-id] {',
    '  --inline-container-max-width: 100% !important;',
    '  --container-max-w: 100% !important;',
    '  max-width: 100% !important;',
    '  width: 100% !important;',
    '  margin: 0 !important;',
    '  display: block !important;',
    '  box-sizing: border-box !important;',
    '}'
  ].join('\n');

  var STYLES = [
    ':host {',
    '  --app-embed-padding-x: 0 !important;',
    '  --app-embed-padding-y: 0 !important;',
    '  --spacing-size-x: 0 !important;',
    '  --container-max-w: 100% !important;',
    '  --inline-container-max-width: 100% !important;',
    '  --forms-padding-top: 0 !important;',
    '  --forms-padding-bottom: 0 !important;',
    '  --container-text-alignment: left !important;',
    '  display: block !important;',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '}',
    '*, *::before, *::after {',
    '  box-sizing: border-box !important;',
    '  min-width: 0 !important;',
    '}',
    '[class*="_container_"],',
    '[data-testid="app-router"],',
    '[data-sizing="form-wrapper"],',
    '[class*="_inline_"],',
    '[class*="_grid_"],',
    '[class*="_gridItem_"],',
    '[class*="_gridItemContent_"] {',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  padding: 0 !important;',
    '}',
    '[class*="_noImage_"] [class*="_gridItem_"]:first-child,',
    '[class*="_imageLoading_"],',
    '[class*="_formHeader_"],',
    '[class*="_formFieldset_"] > div:has(shop-lead-capture) {',
    '  display: none !important;',
    '}',
    '[class*="_formContainer_"] {',
    '  background: transparent !important;',
    '  box-shadow: none !important;',
    '  border: 0 !important;',
    '  border-radius: 0 !important;',
    '  padding: 0 !important;',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  margin: 0 !important;',
    '  grid-template-columns: 1fr !important;',
    '}',

    /* ---- layout: 6-column grid, every field full width by default ---- */
    'form[class*="_formFieldset_"] {',
    '  display: grid !important;',
    '  grid-template-columns: repeat(6, minmax(0, 1fr)) !important;',
    '  column-gap: 20px !important;',
    '  row-gap: 0 !important;',
    '  align-items: end !important;',
    '  border: 0 !important;',
    '  padding: 0 !important;',
    '  margin: 0 !important;',
    '  width: 100% !important;',
    '}',
    'form[class*="_formFieldset_"] > * {',
    '  grid-column: 1 / -1 !important;',
    '  margin: 0 0 16px !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name),',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#last_name) {',
    '  grid-column: span 3 !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#city"]),',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#province"]),',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#postal_code"]) {',
    '  grid-column: span 2 !important;',
    '}',
    '@media (max-width: 749px) {',
    '  form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name),',
    '  form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#last_name),',
    '  form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#city"]),',
    '  form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#province"]),',
    '  form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has([id="custom#postal_code"]) {',
    '    grid-column: 1 / -1 !important;',
    '  }',
    '}',

    /* ---- field container: static label above input ---- */
    '[class*="_formFieldContainer_"] {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '  position: relative !important;',
    '}',
    '[class*="_formFieldContainer_"] > [class*="_formInputFieldLabel_"] { order: 1 !important; }',
    '[class*="_formFieldContainer_"] > [class*="_formInputField_"],',
    '[class*="_formFieldContainer_"] > [class*="_formPhoneInputField_"] { order: 2 !important; }',
    '[class*="_formFieldContainer_"] > [class*="_formError_"] { order: 3 !important; }',

    /* ---- group titles (NAME / EMAIL / PHONE / ADDRESS), as on the original ---- */
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name)::before { content: "Name"; }',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email)::before { content: "Email"; }',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"]::before { content: "Phone"; }',
    'form[class*="_formFieldset_"] > [class*="_selectContainer_"]:has([id="custom#country-selector-label"])::before { content: "Address"; }',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#first_name)::before,',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email)::before,',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"]::before,',
    'form[class*="_formFieldset_"] > [class*="_selectContainer_"]:has([id="custom#country-selector-label"])::before,',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] > p {',
    '  display: block !important;',
    '  order: -2 !important;',
    '  flex: 0 0 auto !important;',
    '  font-family: var(--lxiii-font-heading, serif) !important;',
    '  font-size: 17px !important;',
    '  font-weight: 400 !important;',
    '  letter-spacing: 0.06em !important;',
    '  text-transform: uppercase !important;',
    '  color: #222222 !important;',
    '  line-height: 1.4 !important;',
    '  margin: 0 0 12px !important;',
    '  padding: 0 !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email),',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"],',
    'form[class*="_formFieldset_"] > [class*="_selectContainer_"]:has([id="custom#country-selector-label"]),',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] {',
    '  margin-top: 18px !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] {',
    '  border-top: 1px solid #c9c9c9 !important;',
    '  padding-top: 26px !important;',
    '  margin-top: 10px !important;',
    '}',

    /* ---- "(required)" next to group titles (Name, Email, Phone, Receipt) ---- */
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email) {',
    '  flex-direction: row !important;',
    '  flex-wrap: wrap !important;',
    '  align-items: baseline !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email) > * {',
    '  flex: 0 0 100% !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFieldContainer_"]:has(#email)::after,',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"]::after,',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] > p::after,',
    'label[for="first_name"]::after,',
    'label[for="last_name"]::after,',
    'label[for="custom#address_line_1"]::after,',
    'label[for="custom#city"]::after,',
    'label[for="custom#province"]::after,',
    'label[for="custom#postal_code"]::after {',
    '  content: "(required)" !important;',
    '  order: -1 !important;',
    '  flex: 1 1 auto !important;',
    '  margin: 0 0 12px 6px !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 11px !important;',
    '  font-weight: 300 !important;',
    '  letter-spacing: 0.02em !important;',
    '  text-transform: none !important;',
    '  color: #777777 !important;',
    '  line-height: 1.4 !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] > p::after,',
    'label[for="first_name"]::after,',
    'label[for="last_name"]::after,',
    'label[for="custom#address_line_1"]::after,',
    'label[for="custom#city"]::after,',
    'label[for="custom#province"]::after,',
    'label[for="custom#postal_code"]::after {',
    '  display: inline !important;',
    '  order: 1 !important;',
    '  margin: 0 !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] > p::after { margin-left: 6px !important; }',

    /* ---- Country: shipping note next to the label ---- */
    '[class*="_selectContainer_"] > [id="custom#country-selector-label"]::after {',
    '  content: "(Shipping to Canada only)" !important;',
    '  display: inline !important;',
    '  order: 1 !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 11px !important;',
    '  font-weight: 300 !important;',
    '  letter-spacing: 0.02em !important;',
    '  text-transform: none !important;',
    '  color: #777777 !important;',
    '}',

    /* ---- shipping delay under the submit button ---- */
    'form[class*="_formFieldset_"]::after {',
    '  content: "Shipping within 30 business days" !important;',
    '  grid-column: 1 / -1 !important;',
    '  display: block !important;',
    '  margin: 14px 0 0 !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 12px !important;',
    '  font-weight: 300 !important;',
    '  letter-spacing: 0.02em !important;',
    '  color: #555555 !important;',
    '  line-height: 1.5 !important;',
    '}',

    /* ---- no small label for Email and Phone (group title is enough) ---- */
    'label[for="email"], label[for="phone_number"] { display: none !important; }',

    /* ---- receipt upload: hint text between the title and the drop zone ---- */
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"]::before {',
    '  content: "Please upload a photo or scan of your receipt of purchase to qualify" !important;',
    '  display: block !important;',
    '  order: 1 !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 13px !important;',
    '  font-weight: 300 !important;',
    '  color: #777777 !important;',
    '  line-height: 1.5 !important;',
    '  margin: 0 0 14px !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formFileInput_"] > [class*="_formFileInputField_"] { order: 2 !important; }',

    /* ---- labels ---- */
    '[class*="_formInputFieldLabel_"],',
    '[class*="_selectContainer_"] > [class*="_label_"] {',
    '  position: static !important;',
    '  top: auto !important;',
    '  left: auto !important;',
    '  transform: none !important;',
    '  transition: none !important;',
    '  display: flex !important;',
    '  flex-wrap: wrap !important;',
    '  align-items: baseline !important;',
    '  column-gap: 6px !important;',
    '  white-space: nowrap !important;',
    '  pointer-events: auto !important;',
    '  background: transparent !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 12px !important;',
    '  font-weight: 400 !important;',
    '  letter-spacing: 0.02em !important;',
    '  text-transform: none !important;',
    '  color: #555555 !important;',
    '  line-height: 1.4 !important;',
    '  margin: 0 0 8px !important;',
    '  padding: 0 !important;',
    '}',

    /* ---- inputs, selects ---- */
    '[class*="_formInputField_"],',
    '[class*="_formPhoneInputField_"],',
    '[class*="_selectToggle_"] {',
    '  width: 100% !important;',
    '  height: 42px !important;',
    '  min-height: 42px !important;',
    '  padding: 8px 12px !important;',
    '  background: #ffffff !important;',
    '  border: 0 !important;',
    '  border-bottom: 1px solid #222222 !important;',
    '  border-radius: 0 !important;',
    '  box-shadow: none !important;',
    '  outline: none !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 14px !important;',
    '  font-weight: 300 !important;',
    '  color: #222222 !important;',
    '  text-align: left !important;',
    '}',
    '[class*="_selectToggle_"] {',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  justify-content: space-between !important;',
    '  cursor: pointer !important;',
    '}',
    '[class*="_selectToggleText_"] {',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 14px !important;',
    '  font-weight: 300 !important;',
    '  color: #222222 !important;',
    '}',
    '[class*="_formInputField_"]:focus,',
    '[class*="_formPhoneInputField_"]:focus,',
    '[class*="_selectToggle_"]:focus,',
    '[class*="_selectToggle_"]:focus-visible {',
    '  border-bottom-color: #000000 !important;',
    '  box-shadow: none !important;',
    '  outline: none !important;',
    '}',
    '[class*="_formInputField_"]::placeholder,',
    '[class*="_formPhoneInputField_"]::placeholder {',
    '  color: #b5b5b5 !important;',
    '  font-weight: 300 !important;',
    '}',

    /* ---- phone: title on its own line, flag selector + number on the next ---- */
    '[class*="_formPhoneInputContainer_"] {',
    '  display: grid !important;',
    '  grid-template-columns: max-content minmax(0, 1fr) !important;',
    '  column-gap: 8px !important;',
    '  row-gap: 0 !important;',
    '  align-items: end !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"]::before { grid-column: 1 !important; grid-row: 1 !important; }',
    'form[class*="_formFieldset_"] > [class*="_formPhoneInputContainer_"]::after { grid-column: 2 !important; grid-row: 1 !important; justify-self: start !important; margin-left: 0 !important; }',
    '[class*="_formPhoneInputContainer_"] .phone-country-selector { grid-column: 1 !important; grid-row: 2 !important; }',
    '[class*="_formPhoneInputContainer_"] .phone-country-selector [class*="_selectContainer_"] { margin: 0 !important; }',
    '[class*="_formPhoneInputContainer_"] .phone-country-selector [class*="_selectToggle_"] {',
    '  width: auto !important;',
    '  min-width: 0 !important;',
    '  padding: 8px 10px !important;',
    '  gap: 8px !important;',
    '}',
    '[class*="_formPhoneInputContainer_"] > [class*="_formFieldContainer_"] { grid-column: 2 !important; grid-row: 2 !important; margin: 0 !important; }',

    /* ---- consent checkbox: round, as on the original ---- */
    'form[class*="_formFieldset_"] > [class*="_consentCheckboxContainer_"] {',
    '  margin: -6px 0 16px !important;',
    '}',
    '[class*="_checkboxFieldSet_"] { border: 0 !important; padding: 0 !important; margin: 0 !important; }',
    '[class*="_checkboxField_"] {',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  gap: 10px !important;',
    '  cursor: pointer !important;',
    '  margin: 0 !important;',
    '}',
    '[class*="_checkboxWrapper_"] { display: flex !important; align-items: center !important; }',
    '[class*="_checkboxInput_"] {',
    '  appearance: none !important;',
    '  -webkit-appearance: none !important;',
    '  width: 18px !important;',
    '  height: 18px !important;',
    '  margin: 0 !important;',
    '  border: 1px solid #444444 !important;',
    '  border-radius: 50% !important;',
    '  background: transparent !important;',
    '  cursor: pointer !important;',
    '  position: relative !important;',
    '  box-shadow: none !important;',
    '  outline: none !important;',
    '}',
    '[class*="_checkboxInput_"]:checked::after {',
    '  content: "" !important;',
    '  position: absolute !important;',
    '  inset: 3px !important;',
    '  border-radius: 50% !important;',
    '  background: #1a1a1a !important;',
    '}',
    '[class*="_checkboxInput_"]:checked { background: transparent !important; }',
    '[class*="_checkboxField_"] > [class*="_textBody_"] {',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 12px !important;',
    '  font-weight: 300 !important;',
    '  color: #444444 !important;',
    '}',

    /* ---- file upload: dashed drop zone with a "+" ---- */
    '[class*="_formFileInputField_"] {',
    '  display: block !important;',
    '  position: relative !important;',
    '  width: 100% !important;',
    '  padding: 22px 16px !important;',
    '  border: 1px dashed #999999 !important;',
    '  border-radius: 0 !important;',
    '  background: transparent !important;',
    '  text-align: center !important;',
    '  cursor: pointer !important;',
    '}',
    '[class*="_formFileInputField_"]:hover { border-color: #1a1a1a !important; }',
    '[class*="_formFileInputLabel_"] {',
    '  position: absolute !important;',
    '  inset: 0 !important;',
    '  cursor: pointer !important;',
    '  z-index: 1 !important;',
    '}',
    '[class*="_formFileInputNativeInput_"] { opacity: 0 !important; width: 100% !important; height: 100% !important; cursor: pointer !important; }',
    '[class*="_formFileInputContent_"] {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '  align-items: center !important;',
    '  gap: 8px !important;',
    '  pointer-events: none !important;',
    '}',
    '[class*="_formFileInputContent_"]::before {',
    '  content: "+" !important;',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  width: 34px !important;',
    '  height: 34px !important;',
    '  border-radius: 50% !important;',
    '  background: #ffffff !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 22px !important;',
    '  font-weight: 300 !important;',
    '  line-height: 1 !important;',
    '  color: #222222 !important;',
    '}',
    '[class*="_formFileInputButton_"] {',
    '  background: transparent !important;',
    '  border: 0 !important;',
    '  padding: 0 !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 13px !important;',
    '  font-weight: 300 !important;',
    '  letter-spacing: 0 !important;',
    '  text-transform: none !important;',
    '  color: #333333 !important;',
    '}',

    /* ---- submit ---- */
    'form[class*="_formFieldset_"] > [class*="_formSubmitButton_"] {',
    '  grid-column: 1 / -1 !important;',
    '  justify-self: start !important;',
    '  display: inline-block !important;',
    '  width: auto !important;',
    '  min-width: 0 !important;',
    '  margin: 0 !important;',
    '  padding: 12px 26px !important;',
    '  background: #111111 !important;',
    '  color: #ffffff !important;',
    '  border: 0 !important;',
    '  border-radius: 4px !important;',
    '  box-shadow: none !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 14px !important;',
    '  font-weight: 400 !important;',
    '  letter-spacing: 0.12em !important;',
    '  text-transform: uppercase !important;',
    '  cursor: pointer !important;',
    '  transition: opacity 0.2s ease !important;',
    '}',
    'form[class*="_formFieldset_"] > [class*="_formSubmitButton_"]:hover {',
    '  opacity: 0.8 !important;',
    '  background: #111111 !important;',
    '}',

    /* ---- errors ---- */
    '[class*="_formError_"],',
    '[class*="_error_"] {',
    '  display: block !important;',
    '  margin: 6px 0 0 !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 12px !important;',
    '  line-height: 1.4 !important;',
    '  color: #c71b1b !important;',
    '}',
    '[class*="_withError_"] [class*="_formInputField_"],',
    '[class*="_withError_"] [class*="_formPhoneInputField_"],',
    '[class*="_withError_"] [class*="_selectToggle_"] {',
    '  border-color: #c71b1b !important;',
    '}',

    /* ---- disclaimer ---- */
    '[class*="_formDisclaimer_"] {',
    '  margin-top: 18px !important;',
    '  font-family: var(--lxiii-font-body, sans-serif) !important;',
    '  font-size: 12px !important;',
    '  font-weight: 300 !important;',
    '  color: #777777 !important;',
    '  text-align: left !important;',
    '}',
    '[class*="_formDisclaimer_"] a {',
    '  color: #222222 !important;',
    '  text-decoration: underline !important;',
    '}'
  ].join('\n');

  function injectIntoShadowRoot(shadowRoot) {
    if (!shadowRoot || shadowRoot.querySelector('style[data-lxiii-form-styler]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-lxiii-form-styler', '');
    style.textContent = STYLES;
    shadowRoot.appendChild(style);
  }

  function injectHostStyles() {
    if (document.querySelector('style[data-lxiii-form-styler-host]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-lxiii-form-styler-host', '');
    style.textContent = HOST_STYLES;
    document.head.appendChild(style);
  }

  var scrollbarGuarded = new WeakSet();
  function preventScrollbarBlur(shadowRoot) {
    if (scrollbarGuarded.has(shadowRoot)) return;
    scrollbarGuarded.add(shadowRoot);
    shadowRoot.addEventListener('mousedown', function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[class*="_selectContainer_"], [id$="-selector-dropdown"], [role="listbox"]')) return;
      if (e.offsetX >= target.clientWidth || e.offsetY >= target.clientHeight) e.preventDefault();
    }, true);
  }

  /* The Forms app has no default value for dropdowns: pick "Canada" in the
     Country select on load by driving the app's own toggle + option. */
  var DEFAULT_COUNTRY = 'Canada';
  var countryPreselected = new WeakSet();
  var countryAttempts = 0;
  function preselectCountry(shadowRoot) {
    if (countryPreselected.has(shadowRoot) || countryAttempts > 40) return;
    var valueSpan = shadowRoot.querySelector('[id="custom#country-selector-value"]');
    var toggle = valueSpan && valueSpan.closest('button');
    if (!toggle) return;
    if (valueSpan.textContent.trim() !== '') { countryPreselected.add(shadowRoot); return; }
    countryAttempts++;
    var scrollY = window.scrollY;
    toggle.click();
    setTimeout(function () {
      var options = shadowRoot.querySelectorAll('[role="option"], [id="custom#country-selector-dropdown"] li, [id="custom#country-selector-dropdown"] button');
      var match = null;
      options.forEach(function (o) { if (!match && o.textContent.trim() === DEFAULT_COUNTRY) match = o; });
      if (match) {
        match.click();
        countryPreselected.add(shadowRoot);
      } else if (toggle.getAttribute('aria-expanded') === 'true') {
        toggle.click();
      }
      if (shadowRoot.activeElement && shadowRoot.activeElement.blur) shadowRoot.activeElement.blur();
      window.scrollTo(window.scrollX, scrollY);
    }, 60);
  }

  function tryStyleAll() {
    injectHostStyles();
    document.querySelectorAll('shopify-forms-embed').forEach(function (host) {
      if (!host.shadowRoot) return;
      injectIntoShadowRoot(host.shadowRoot);
      preventScrollbarBlur(host.shadowRoot);
      preselectCountry(host.shadowRoot);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryStyleAll);
  } else {
    tryStyleAll();
  }

  new MutationObserver(tryStyleAll).observe(document.documentElement, { childList: true, subtree: true });

  var retries = 0;
  var retryTimer = setInterval(function () {
    tryStyleAll();
    if (++retries >= 10) clearInterval(retryTimer);
  }, 300);
})();
